import React, { useState, useEffect } from 'react';
import { usePropertyData } from '@/hooks/admin/usePropertyData';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { patchAdminProperty } from '@/API/apiService';

interface SortableProperty {
  property_id: number;
  site_controller_id: number;
  name: string;
  address: string;
  is_published: boolean;
  sort_number: number;
}

interface SortableRowProps {
  item: SortableProperty;
  onTogglePublished: (property_id: number) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  item,
  onTogglePublished,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.property_id.toString(),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex border-b border-[#DDDDDD] bg-white ${isDragging ? 'bg-gray-100' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* ドラッグハンドルセル */}
      <div className="w-1/12 p-3">
        <div className="cursor-grab select-none inline-block">≡</div>
      </div>
      <div className="w-3/12 p-3">{item.name}</div>
      <div className="w-3/12 p-3">{item.address}</div>
      {/* 公開／非公開セル：クリックでトグル */}
      <div
        className="w-2/12 p-3 cursor-pointer"
        onClick={() => onTogglePublished(item.property_id)}
      >
        {item.is_published ? '公開' : '非公開'}
      </div>
      <div className="w-3/12 p-3">
        <Link
          to={`/admin/property/${item.property_id}/edit`}
          className="bg-white px-2 py-1 border rounded text-xs cursor-pointer text-center"
        >
          物件情報編集
        </Link>
        <Link
          to={`/admin/property/${item.property_id}/room`}
          className="underline text-black cursor-pointer text-xs p-[6px] font-yugothic ml-2"
        >
          部屋情報一覧
        </Link>
      </div>
    </div>
  );
};

export const TableProperty: React.FC = () => {
  const { properties, loading, error } = usePropertyData();
  const [items, setItems] = useState<SortableProperty[]>([]);
  const [orderChanged, setOrderChanged] = useState(false);

  // APIから取得したフルデータから並べ替えに必要な項目を抽出
  useEffect(() => {
    const mapped: SortableProperty[] = properties.map((item: any) => ({
      property_id: item.property_id,
      site_controller_id: item.site_controller_id,
      name: item.name,
      address: item.address,
      // is_published を boolean に明示的に変換する（1や"1"なら true とする）
      is_published:
        item.is_published === true ||
        item.is_published === 1 ||
        item.is_published === '1',
      sort_number: item.sort_number !== undefined ? item.sort_number : 0,
    }));
    // sort_number でソートし、index+1 の値で再計算
    const sorted = mapped.sort((a, b) => a.sort_number - b.sort_number);
    const recalculated = sorted.map((item, index) => ({
      ...item,
      sort_number: index + 1,
    }));
    setItems(recalculated);
    setOrderChanged(false);
  }, [properties]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // ドラッグ終了時に items の順序を更新
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex: number = items.findIndex(
      (item) => item.property_id.toString() === active.id
    );
    const newIndex: number = items.findIndex(
      (item) => item.property_id.toString() === over.id
    );
    const newItems: SortableProperty[] = arrayMove(
      items,
      oldIndex,
      newIndex
    ).map((item, index) => ({
      ...item,
      sort_number: index + 1,
    }));
    setItems(newItems);
    setOrderChanged(true);
  };

  // 公開状態をトグルする処理
  const handleTogglePublished = (property_id: number) => {
    const newItems = items.map((item) =>
      item.property_id === property_id
        ? { ...item, is_published: !item.is_published }
        : item
    );
    setItems(newItems);
    setOrderChanged(true);
  };

  // 並べ替え・変更確定時の更新処理（sort_number と is_published を更新）
  const confirmSortUpdate = async () => {
    try {
      await Promise.all(
        items.map((item) => {
          const fullProperty = properties.find(
            (p: any) => p.property_id === item.property_id
          );
          if (!fullProperty) return Promise.resolve();

          // 必須フィールドチェック
          if (
            fullProperty.site_controller_id === undefined ||
            fullProperty.site_controller_id === null ||
            fullProperty.site_controller_property_id === undefined ||
            fullProperty.site_controller_property_id === null ||
            fullProperty.site_controller_property_id.toString().trim() === ''
          ) {
            throw new Error(
              `Property ${fullProperty.property_id} に必要な site_controller の値がありません。`
            );
          }

          const updatedProperty = {
            ...fullProperty,
            sort_number: item.sort_number,
            is_published: item.is_published,
            site_controller_id: fullProperty.site_controller_id,
            site_controller_property_id:
              fullProperty.site_controller_property_id.toString(),
          };

          const fd = new FormData();
          Object.entries(updatedProperty).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              let finalValue: string;
              if (typeof value === 'boolean') {
                finalValue = value ? '1' : '0';
              } else {
                finalValue = value.toString();
              }
              fd.append(key, finalValue);
            }
          });

          return patchAdminProperty(updatedProperty.property_id.toString(), fd);
        })
      );
      setOrderChanged(false);
    } catch (err) {
      console.error('Failed to update property order:', err);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="overflow-x-auto p-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="table w-full border border-[#DDDDDD]">
          {/* ヘッダー */}
          <div className="flex bg-white border-b-2 border-[#DDDDDD]">
            <div className="w-1/12 p-3 text-left text-black text-sm">
              並び替え
            </div>
            <div className="w-3/12 p-3 text-left text-black text-sm">
              物件名
            </div>
            <div className="w-3/12 p-3 text-left text-black text-sm">
              物件所在地
            </div>
            <div className="w-2/12 p-3 text-left text-black text-sm">
              ステータス
            </div>
            <div className="w-3/12 p-3 text-left text-black text-sm">操作</div>
          </div>
          {/* ボディ部分 */}
          <SortableContext
            items={items.map((item) => item.property_id.toString())}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableRow
                key={item.property_id.toString()}
                item={item}
                onTogglePublished={handleTogglePublished}
              />
            ))}
          </SortableContext>
        </div>
      </DndContext>
      {orderChanged && (
        <div className="mt-4 text-center">
          <button
            onClick={confirmSortUpdate}
            className="px-4 py-2 bg-black text-white rounded"
          >
            確定
          </button>
        </div>
      )}
    </div>
  );
};

export default TableProperty;
